/**
 * The catalog panel (Figma «Catalog», nodes 412:108175, 412:108390, 412:109447, 412:109907, 412:110182): a card of
 * 540 × 700 over the stage's bottom-right corner. Under the title, the search field; at the left the app's own tree —
 * each top section with its picture, its subsections as rows, a subsection's lists under it once opened — and at
 * the right the chosen list's chips and its models in two columns. A star on a card keeps the model in «Избранное»,
 * a list of this browser's.
 *
 * A list reads as the app's does (FurnitureItemsLoader): the catalog's order, page after page as the grid scrolls,
 * the next page asked for before the last row comes into sight. A card is named the app's way, out of the model's
 * definition (brand and collection in bold, the caption after), and wears the app's NEW, 2D and PRO marks
 * (catalogRules.ts). Typing turns the panel into «Поиск по каталогу» (catalogSearch.ts): the recent searches, what
 * people search for most and the categories behind it while the field is empty, the popular queries and the
 * categories that fit the typed letters while it is not, and after Enter the models the catalog answers with.
 *
 * Not a modal: cards are draggable straight onto the scene, and a tap places the item in the middle of the view.
 * Opaque rather than translucent on purpose — a blurred panel over a live 3D canvas re-blurs the frame on every
 * scroll tick and costs more than it looks. Every list answers on the next paint: a skeleton while the page is on
 * its way, the cards once it is here, skeleton cards at the end while the next page comes, an honest note when there
 * is nothing or the catalog could not be asked.
 *
 * The properties sheet mounts the same panel inside itself to replace a door, a window or a model (`openingKind`,
 * `onClose`): there it fills the sheet, with one fitting list for an opening and the whole tree for a model.
 *
 * «Выбрать шторы» opens it on the curtains of one door, window or arch (`curtains`, the app's AddCurtains and
 * ReplaceCurtains): the list of models tagged Curtains, «Без штор» first, the one hanging marked; a tap hangs the
 * card in place of what hung there and the panel stays for the next try, as the app's list does.
 */
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent, type RefObject } from 'react';
import { catalogTree, findCatalogList, loadList, placementOf, searchCatalog, type CatalogFilter, type CatalogItem, type CatalogNode, type CatalogPage, type PageCursor } from '../../state/catalog';
import { briefName, cardDefinition, cardDefinitionDue, cardDefinitionNow, type CardDefinition } from '../../state/catalogCard';
import { is2dCard, isNew, isProCard, proPositions, LIST_GALLERY_PAGE, SEARCH_GALLERY_PAGE, type CardFacts } from '../../state/catalogRules';
import {
  HISTORY_SIZE, capitalized, captionIn, categorySuggestions, highlight, loadSearchData, popularCategories, popularQueries, searchDataNow, withSearch,
  type CategoryTarget, type SearchData,
} from '../../state/catalogSearch';
import { catalogOpeningKindOf } from '../../state/catalogPlacement';
import { store } from '../../state/store';
import { isStairs } from '../../core/hp/furnitureClass';
import type { Item } from '../../core/hp/types';
import { Icon } from '../Icon';
import { useStore } from '../useStore';
import { HorizontalStrip } from '../HorizontalStrip';
import { t as tr } from '../../i18n/I18n';

interface ReadyListing {
  status: 'ready';
  items: CatalogItem[];
  /** The models the catalog answered so far, shown or not: the app's estimate of the list's size reads it. */
  read: number;
  /** The app's gallery page for this listing: 35 for a list, 20 for a search. */
  gallery: number;
  next: PageCursor;
  more: boolean;
  /** A search's words, by pass; absent for a list. */
  terms?: string[];
  /** The next page could not be read; the end of the grid offers to ask again. */
  moreError?: string;
}
type Listing = { status: 'loading' } | { status: 'error'; message: string } | ReadyListing;
/** A list of the tree with the folders above it, so choosing it can open them. */
type ListEntry = { node: CatalogNode; path: CatalogNode[] };

/**
 * A card as it is picked: `pro` when it wore the PRO mark, which the app writes into the piece it adds (`AddedAsPro`);
 * `variationId`, its definition's first variation, which that piece wears.
 */
export type CatalogPick = CatalogItem & { pro?: boolean; variationId?: string };
/** The card as the list shows it now; null when the list no longer holds it, undefined while the list is not in. */
export type CurrentPick = (catalogId: string) => CatalogPick | null | undefined;

/** The card the stage's drop handler reads: dragging carries the whole item, not a lookup. */
export function dragItemData(item: CatalogPick): string { return JSON.stringify(item); }
export function dragItemOf(data: string): CatalogPick | null {
  try { const item = JSON.parse(data); return item && typeof item.id === 'string' && Array.isArray(item.size) ? item as CatalogPick : null; } catch { return null; }
}

/** A card that builds a wall, an opening or stairs: a shared project leaves those to the app. */
export function buildsArchitecture(item: Pick<CatalogItem, 'tags' | 'builtByCode'>): boolean {
  return item.builtByCode === 'wall' || !!catalogOpeningKindOf(item) || isStairs({ tags: item.tags } as Item);
}

/** The list that stands for a door or a window, for the opening replacement flow. */
function openingList(kind: 'door' | 'window'): CatalogNode {
  const name = kind === 'door' ? 'Doors' : 'Windows';
  return findCatalogList(name) ?? { id: name, name, title: kind === 'door' ? tr('Doors') : tr('Windows'), anyTags: [], allTags: [kind === 'door' ? 'Door' : 'Window'] };
}
/** ReplaceCurtains' own list: the models tagged Curtains, which an opening then finds as its curtains. */
const CURTAINS_LIST: CatalogNode = { id: 'curtains-for-opening', name: 'Curtains', get title() { return tr('Curtains'); }, anyTags: [], allTags: ['Curtains'] };

/** «Выбрать шторы»: the curtain hanging over the opening, by model id, or null; `bare` when nothing hangs there at all. */
export interface CurtainChoice { current: string | null; bare: boolean; onNone: () => void }
type FixedKind = 'door' | 'window' | 'curtains';

const RECENT_KEY = 'hp.catalog.recent', SAVED_KEY = 'hp.catalog.saved', SAVED_ID = '__saved';
/** SearchesController.MaxItems: the rows under the field. */
const SUGGESTION_ROWS = 4;
/** How far ahead of the grid's end the next page is asked for: about two rows of cards. */
const PRELOAD_PX = 420;
function readJson<T>(key: string, fallback: T): T {
  try { const raw = localStorage.getItem(key); return raw ? JSON.parse(raw) as T : fallback; } catch { return fallback; }
}
/**
 * The starred cards, each placed by its tags under today's rules rather than the answer stored when it was starred; an
 * entry that is no card, which this browser's storage may hold, is left out.
 */
export function savedCards(): CatalogItem[] {
  const stored = readJson<unknown>(SAVED_KEY, []);
  return (Array.isArray(stored) ? stored : [])
    .filter((item): item is CatalogItem => typeof item === 'object' && item !== null && Array.isArray(item.tags) && typeof item.catalogId === 'string')
    .map(item => ({ ...item, placement: placementOf(item.tags) }));
}
function writeJson(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* blocked or full storage: the lists are a convenience of this browser */ }
}
function allLists(nodes: CatalogNode[], path: CatalogNode[] = []): ListEntry[] {
  return nodes.flatMap(node => node.children?.length ? allLists(node.children, [...path, node]) : [{ node, path }]);
}
/** A suggested list or subcategory as what it opens: the list, narrowed by the chip for a subcategory. */
function entryOf(target: CategoryTarget): { entry: ListEntry; filter?: CatalogFilter } {
  return { entry: { node: target.node, path: target.path }, filter: target.filter };
}
/** A suggested category's picture: its own, else its first list's, else the nearest folder's above it. */
function photoOf(target: CategoryTarget): string | undefined {
  if (target.node.photo) return target.node.photo;
  const inside = target.node.children?.length ? allLists(target.node.children).find(entry => entry.node.photo)?.node.photo : undefined;
  return inside ?? [...target.path].reverse().find(node => node.photo)?.photo;
}
const sizeLabel = (item: CatalogItem) => tr('#ValueCentimeters', `${(item.size[0] * 100).toFixed(0)} × ${(item.size[2] * 100).toFixed(0)}`);
/** Keeps the search field focused while a suggestion is clicked: the click, not the blur, decides. */
const keepFocus = (event: ReactMouseEvent) => event.preventDefault();
const messageOf = (error: unknown) => error instanceof Error ? error.message : String(error);

/** The first page of a listing. */
function firstListing(page: CatalogPage, terms?: string[]): ReadyListing {
  return { status: 'ready', items: unique(page.items, new Set()), read: page.raw, gallery: terms ? SEARCH_GALLERY_PAGE : LIST_GALLERY_PAGE,
    next: page.next, more: page.more, ...(terms ? { terms } : {}) };
}
/** The listing with the next page after it; a model a later search finds again is not shown twice. */
function withPage(listing: ReadyListing, page: CatalogPage): ReadyListing {
  const fresh = unique(page.items, new Set(listing.items.map(item => item.catalogId)));
  return { ...listing, items: [...listing.items, ...fresh], read: listing.read + page.raw, next: page.next, more: page.more, moreError: undefined };
}
function unique(items: CatalogItem[], seen: Set<string>): CatalogItem[] {
  return items.filter(item => { if (seen.has(item.catalogId)) return false; seen.add(item.catalogId); return true; });
}

export function CatalogDrawer({ onPlace, onClose, openingKind, initialSection, curtains, closing = false, currentPick, placing = null }: {
  onPlace: (item: CatalogPick, at?: { x: number; y: number }) => void; onClose?: () => void; openingKind?: 'door' | 'window';
  /** The list to open on, by the tree's id or name: the object toolbar's «+» names one (`contextToolbar.ts`). */
  initialSection?: string | null; closing?: boolean;
  /** Curtains for one opening instead of models to place: see the note above. */
  curtains?: CurtainChoice;
  /** Filled with a reader of the listed cards while the panel is open. */
  currentPick?: RefObject<CurrentPick | null>;
  /** The catalog id of the card whose placement waits for its model: that card is busy. */
  placing?: string | null;
}) {
  const embedded = !!onClose;
  const shared = useStore(state => state.edit === 'items');
  const lists = useMemo(() => allLists(catalogTree()), []);
  const kind: FixedKind | undefined = curtains ? 'curtains' : openingKind;
  const fixed = useMemo(() => kind === 'curtains' ? CURTAINS_LIST : kind ? openingList(kind) : null, [kind]);
  const initial = useMemo(() => lists.find(entry => entry.node.name === initialSection || entry.node.id === initialSection) ?? lists[0], [initialSection, lists]);
  const [selected, setSelected] = useState<ListEntry | null>(() => fixed ? { node: fixed, path: [] } : initial ?? null);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(fixed ? [] : initial?.path.map(node => node.id) ?? []));
  const [filter, setFilter] = useState<CatalogFilter | undefined>();
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  /** The typed words the grid shows the catalog's answer for; empty while a list is shown. */
  const [term, setTerm] = useState('');
  const [listing, setListing] = useState<Listing | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [recent, setRecent] = useState<string[]>(() => readJson<string[]>(RECENT_KEY, []).slice(0, HISTORY_SIZE));
  const [saved, setSaved] = useState<CatalogItem[]>(savedCards);
  const [searchData, setSearchData] = useState<SearchData | null>(() => searchDataNow());
  const [searchDataFailed, setSearchDataFailed] = useState(false);
  /** Bumped as definitions arrive: the cards read them from their cache. */
  const [revision, setRevision] = useState(0);
  const generation = useRef(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Named again while open, the list comes to the front as a tap in the tree would bring it.
  useEffect(() => {
    if (fixed || !initialSection || !initial) return;
    setSelected(initial); setExpanded(new Set(initial.path.map(node => node.id))); setFilter(undefined);
    setTerm(''); setQuery(''); setFocused(false);
  }, [initialSection, initial, fixed]);
  const listingRef = useRef<Listing | null>(listing);
  listingRef.current = listing;
  const savedRef = useRef(saved);
  savedRef.current = saved;
  const loadingMoreRef = useRef(false);
  const bumpFrame = useRef(0);
  const bump = useCallback(() => {
    if (bumpFrame.current) return;
    bumpFrame.current = requestAnimationFrame(() => { bumpFrame.current = 0; setRevision(value => value + 1); });
  }, []);
  // The tap's placement has just read the definition again: the card redraws with the marks the pick carries.
  useImperativeHandle(currentPick, () => (catalogId: string) => {
    const current = listingRef.current;
    if (current?.status !== 'ready') return undefined;
    const index = current.items.findIndex(item => item.catalogId === catalogId);
    if (index < 0) return null;
    bump();
    return pickOf(current.items[index], marksOf(current, savedRef.current)[index], false);
  }, [bump]);

  useEffect(() => {
    let alive = true;
    loadSearchData().then(loaded => { if (alive) setSearchData(loaded); }, () => { if (alive) setSearchDataFailed(true); });
    return () => { alive = false; cancelAnimationFrame(bumpFrame.current); bumpFrame.current = 0; };
  }, [bump]);

  const q = query.trim();
  const searching = !fixed && !term && (focused || q.length > 0);
  const showingSaved = !term && selected?.node.id === SAVED_ID;
  const list = term || showingSaved ? null : selected?.node ?? null;
  const listKey = term ? `search:${term}` : list ? `list:${list.id}:${filter?.id ?? ''}` : showingSaved ? 'saved' : '';

  // One request per listing; a stale answer (the person moved on) is dropped, not shown.
  useEffect(() => {
    if (!listKey) { setListing(null); return; }
    const token = ++generation.current;
    loadingMoreRef.current = false;
    setLoadingMore(false);
    gridRef.current?.scrollTo({ top: 0 });
    if (listKey === 'saved') {
      const items = fitting(saved, kind);
      setListing({ status: 'ready', items, read: items.length, gallery: LIST_GALLERY_PAGE, next: { pass: 0, skip: 0 }, more: false });
      return;
    }
    setListing({ status: 'loading' });
    const terms = term ? [term] : undefined;
    (terms ? searchCatalog(terms) : loadList(list!, filter)).then(page => {
      if (token === generation.current) setListing(firstListing(withFitting(page, kind), terms));
    }, error => { if (token === generation.current) setListing({ status: 'error', message: messageOf(error) }); });
    return () => { generation.current += 1; };
  }, [listKey, attempt, showingSaved ? saved : null]);

  const loadMore = useCallback(async () => {
    const current = listingRef.current;
    if (current?.status !== 'ready' || !current.more || current.moreError || loadingMoreRef.current) return;
    const token = generation.current;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const page = current.terms ? await searchCatalog(current.terms, current.next) : await loadList(list!, filter, current.next);
      if (token === generation.current) setListing(withPage(current, withFitting(page, kind)));
    } catch (error) {
      if (token === generation.current) setListing({ ...current, moreError: messageOf(error) });
    } finally {
      if (token === generation.current) { loadingMoreRef.current = false; setLoadingMore(false); }
    }
  }, [list, filter, kind]);

  // The next page is asked for while the last rows are still below the fold (FurnitureItemsLoader.LoadNextPage from
  // the gallery's end); a fresh observer after each page asks again at once when the grid still does not fill.
  const ready = listing?.status === 'ready' ? listing : null;
  useEffect(() => {
    const root = gridRef.current, end = endRef.current;
    if (!root || !end || !ready?.more || ready.moreError || loadingMore || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(entries => { if (entries.some(entry => entry.isIntersecting)) void loadMore(); },
      { root, rootMargin: `0px 0px ${PRELOAD_PX}px 0px` });
    observer.observe(end);
    return () => observer.disconnect();
  }, [ready, loadingMore, loadMore]);

  // Every card's definition, a few at a time; the cards redraw once as they arrive.
  useEffect(() => {
    if (!ready) return;
    for (const item of ready.items) if (item.definition && cardDefinitionDue(item.definition)) void cardDefinition(item.definition).then(bump);
  }, [ready, bump]);

  const close = () => { if (onClose) onClose(); else store.set({ showCatalog: false }); };
  const toggle = (id: string) => setExpanded(current => { const next = new Set(current); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  /** A list from the tree, the search's suggestions or its tiles: shown, with the folders above it open. */
  const choose = (entry: ListEntry, narrowTo?: CatalogFilter) => {
    setSelected(entry); setFilter(narrowTo); setTerm(''); setQuery(''); setFocused(false);
    setExpanded(current => new Set([...current, ...entry.path.map(node => node.id)]));
    inputRef.current?.blur();
  };
  const remember = (text: string) => setRecent(current => { const next = withSearch(current, text); writeJson(RECENT_KEY, next); return next; });
  const forget = (text: string) => setRecent(current => { const next = current.filter(entry => entry !== text); writeJson(RECENT_KEY, next); return next; });
  const commitSearch = (text: string) => {
    const words = text.trim();
    if (!words) return;
    setQuery(words); setTerm(words); setFocused(false); inputRef.current?.blur();
    remember(words);
  };
  /**
   * A suggested category (CatalogSearchLogic.OnRecommendedCategoriesItemClicked): its name goes to the history, and
   * the grid shows its list (with its chip for a subcategory). A folder has no list of its own: the app searches for
   * its name, and so does the panel.
   */
  const chooseCategory = (target: CategoryTarget) => {
    if (target.node.children?.length && !target.filter) { commitSearch(target.title); return; }
    remember(target.title);
    const { entry, filter: narrowTo } = entryOf(target);
    choose(entry, narrowTo);
  };
  const clearSearch = () => { setQuery(''); setTerm(''); inputRef.current?.focus(); };
  const fieldKey = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === 'Enter') { event.preventDefault(); commitSearch(query); }
    else if (event.key === 'Escape') { event.preventDefault(); if (q || term) { setQuery(''); setTerm(''); } else { inputRef.current?.blur(); setFocused(false); close(); } }
  };
  const isSaved = (item: CatalogItem) => saved.some(candidate => candidate.catalogId === item.catalogId);
  const toggleSaved = (item: CatalogItem) => setSaved(current => {
    const next = isSaved(item) ? current.filter(candidate => candidate.catalogId !== item.catalogId) : [item, ...current];
    writeJson(SAVED_KEY, next);
    return next;
  });

  const title = fixed ? fixed.title : searching || term ? tr('#CatalogDrawerSearch') : tr('#CatalogDrawerTitle');
  const filters = !term && list?.filters?.length ? list.filters : undefined;
  const lower = q.toLowerCase();

  // The rows under the field (SearchesController): the recent searches first while it is empty, then what people
  // search for most; while typing, the popular queries that fit.
  const rows = useMemo(() => {
    if (!searching) return [] as Array<{ text: string; recent: boolean }>;
    const out: Array<{ text: string; recent: boolean }> = [];
    if (!q) for (const text of recent) if (out.length < SUGGESTION_ROWS && !out.some(row => row.text === text)) out.push({ text, recent: true });
    for (const text of popularQueries(q ? query : '', SUGGESTION_ROWS, searchData).map(capitalized)) {
      if (out.length >= SUGGESTION_ROWS) break;
      if (!out.some(row => row.text === text)) out.push({ text, recent: false });
    }
    return out;
  }, [searching, q, query, recent, searchData]);
  // The round pictures (PopularSearchesController / RecommendedCategoriesController): the categories behind the most
  // popular queries, or those that fit the typed letters, the popular ones again when none does.
  const defaults = useMemo(() => popularCategories(8, searchData), [searchData]);
  const tiles = useMemo(() => {
    if (!q) return defaults;
    const found = categorySuggestions(q, 10, searchData);
    return found.length ? found : defaults;
  }, [q, defaults, searchData]);
  const searchPending = !searchData && !searchDataFailed;

  const marks = useMemo(() => ready ? marksOf(ready, saved) : [], [ready, saved, revision]);

  return (
    <aside className={`catalog-panel${embedded ? ' is-embedded' : ''}${closing ? ' is-closing' : ''}`} role="dialog" aria-label={title} inert={closing} data-search={searching || undefined}>
      <header className="catalog-head">
        <h2>{title}</h2>
        <button type="button" className="catalog-collapse" aria-label={embedded ? tr('Close') : tr('#CatalogDrawerCollapse')} title={embedded ? tr('Close') : tr('#CatalogDrawerCollapse')} onClick={close}><Icon name="chevronDown" size={24} /></button>
      </header>
      <div className="catalog-search">
        <label className="catalog-search-field">
          <Icon name="searchSmall" size={16} />
          <input ref={inputRef} type="search" value={query} placeholder={kind === 'curtains' ? tr('#CatalogDrawerSearchCurtains') : kind ? (kind === 'door' ? tr('#CatalogDrawerSearchDoor') : tr('#CatalogDrawerSearchWindow')) : tr('#CatalogDrawerSearch')} aria-label={tr('#CatalogDrawerSearch')}
            autoComplete="off" enterKeyHint="search" onChange={event => { setQuery(event.target.value); if (term) setTerm(''); }} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} onKeyDown={fieldKey} />
          {(q || term) && <button type="button" className="catalog-search-clear" aria-label={tr('#CatalogDrawerClearSearch')} onMouseDown={keepFocus} onClick={clearSearch}><Icon name="close" size={14} /></button>}
        </label>
      </div>
      {searching ? <div className="catalog-search-view">
        <ul className="catalog-search-list" aria-label={q ? tr('#CatalogDrawerSuggestions') : tr('#CatalogDrawerRecentPopular')}>
          {rows.map(row => { const parts = q ? highlight(row.text, lower) : null; return <li key={(row.recent ? 'r:' : 'p:') + row.text} className="catalog-search-item">
            <button type="button" className="catalog-search-row" onMouseDown={keepFocus} onClick={() => commitSearch(row.text)}>
              <Icon name={row.recent ? 'recent' : 'searchSmall'} size={20} />
              <span>{parts ? <>{parts.before}{parts.match}<b>{parts.after}</b></> : row.text}</span>
            </button>
            {row.recent && <button type="button" className="catalog-search-forget" aria-label={tr('#CatalogDrawerForgetRecentNamed', row.text)} title={tr('#CatalogDrawerForgetRecent')}
              onMouseDown={keepFocus} onClick={() => forget(row.text)}><Icon name="close" size={14} /></button>}
          </li>; })}
          {q && <li><button type="button" className="catalog-search-row is-query" onMouseDown={keepFocus} onClick={() => commitSearch(query)}><Icon name="searchSmall" size={20} /><span>{tr('#CatalogDrawerSearchEverywhere', q)}</span></button></li>}
        </ul>
        <p className={`catalog-search-caption${q ? ' is-strong' : ''}`}>{q ? tr('#CatalogDrawerGoToCategories') : tr('#CatalogDrawerPopular')}</p>
        {searchPending
          ? <div className="catalog-tiles" aria-busy="true" aria-label={tr('#CatalogDrawerLoadingSuggestions')}>{Array.from({ length: 6 }, (_, index) => <span key={index} className="catalog-tile is-skeleton" aria-hidden="true"><span className="catalog-tile-picture" /><i /></span>)}</div>
          : <div className="catalog-tiles">{tiles.map(target => <Tile key={`${target.node.id}:${target.filter?.id ?? ''}`} target={target} onChoose={chooseCategory} />)}</div>}
      </div> : <div className="catalog-main">
        {!fixed && <nav className="catalog-tree" aria-label={tr('#CatalogDrawerSections')}>
          {saved.length > 0 && <div className="catalog-group">
            <button type="button" className="catalog-category catalog-saved" aria-current={showingSaved || undefined} onClick={() => choose({ node: { id: SAVED_ID, name: SAVED_ID, title: tr('Favorites') }, path: [] })}><Icon name="star" size={14} /><span>{tr('Favorites')}</span></button>
          </div>}
          {catalogTree().map(group => <div key={group.id} className="catalog-group">
            {group.children?.length ? <>
              <div className="catalog-group-head">{group.photo ? <img src={group.photo} alt="" loading="lazy" decoding="async" /> : <span className="catalog-group-blank" />}<span>{group.title}</span></div>
              <TreeRows nodes={group.children} path={[group]} depth={0} expanded={expanded} selectedId={term ? '' : selected?.node.id ?? ''} onToggle={toggle} onChoose={choose} />
            </> : <button type="button" className="catalog-category" aria-current={!term && selected?.node.id === group.id || undefined} onClick={() => choose({ node: group, path: [] })}><span>{group.title}</span></button>}
          </div>)}
        </nav>}
        <section className="catalog-results" aria-label={term ? tr('#CatalogDrawerResults', term) : list?.title ?? title}>
          {filters && <HorizontalStrip className="catalog-filters" shellClassName="catalog-filters-shell" aria-label={tr('#CatalogDrawerRefine')}>
            <button type="button" className="catalog-chip" aria-pressed={!filter} onClick={() => setFilter(undefined)}>{tr('All')}</button>
            {filters.map(candidate => <button key={candidate.id} type="button" className="catalog-chip" aria-pressed={filter?.id === candidate.id} onClick={() => setFilter(filter?.id === candidate.id ? undefined : candidate)}>{candidate.title}</button>)}
          </HorizontalStrip>}
          <div className="catalog-grid-scroll" ref={gridRef}>
            {listing?.status === 'loading' && <div className="catalog-grid" aria-busy="true" aria-label={tr('#CatalogDrawerLoadingModels')}>
              {curtains && <NoCurtainsCard choice={curtains} />}
              {Array.from({ length: curtains ? 5 : 6 }, (_, index) => <SkeletonCard key={index} />)}
            </div>}
            {curtains && listing?.status !== 'loading' && !(ready && (ready.items.length > 0 || loadingMore)) && <div className="catalog-grid"><NoCurtainsCard choice={curtains} /></div>}
            {listing?.status === 'error' && <div className="catalog-empty" role="alert">
              <p><strong>{tr('#CatalogDrawerNoAnswer')}</strong></p><p>{listing.message}</p>
              <button type="button" className="catalog-retry" onClick={() => setAttempt(count => count + 1)}>{tr('#PurchaseRetryLoad')}</button>
            </div>}
            {ready && ready.items.length === 0 && !ready.more && <div className="catalog-empty" role="status">
              <p>{term ? tr('#CatalogDrawerNothingFound', term) : showingSaved ? tr('#CatalogDrawerFavoritesEmpty') : tr('#CatalogDrawerSectionEmpty')}</p>
            </div>}
            {ready && (ready.items.length > 0 || loadingMore) && <div className="catalog-grid" aria-busy={loadingMore || undefined}>
              {curtains && <NoCurtainsCard choice={curtains} />}
              {ready.items.map((item, index) => <Card key={item.catalogId} item={item} mark={marks[index]} captions={searchData?.captions ?? (searchDataFailed ? {} : null)}
                saved={isSaved(item)} starred={!fixed} onPlace={onPlace} onToggleSaved={toggleSaved} placing={placing === item.catalogId}
                chosen={curtains ? curtains.current === item.id : undefined} locked={shared && !curtains && !embedded && buildsArchitecture(item) ? tr('#WebLiveItemsOnly') : undefined} />)}
              {loadingMore && Array.from({ length: 4 }, (_, index) => <SkeletonCard key={`more-${index}`} />)}
            </div>}
            {ready && <div className="catalog-grid-end" ref={endRef}>
              <span className="catalog-sr" role="status">{loadingMore ? tr('#CatalogDrawerLoadingMore') : ''}</span>
              {ready.moreError && <div className="catalog-more-error" role="alert">
                <p>{tr('#CatalogDrawerMoreFailed')}</p>
                <button type="button" className="catalog-retry" onClick={() => { const current = listingRef.current; if (current?.status === 'ready') { setListing({ ...current, moreError: undefined }); } }}>{tr('#PurchaseRetryLoad')}</button>
              </div>}
            </div>}
          </div>
        </section>
      </div>}
    </aside>
  );
}

/** What a card wears: its facts are read, so the marks and the name are the app's. */
interface CardMark { definition: CardDefinition | null | undefined; pro: boolean; isNew: boolean; is2d: boolean }

/**
 * The marks of every card in a listing, as the app sets them page by page (FurnitureItemsLoader): a card's place in
 * the list, less the NEW cards up to the end of its gallery page, against the estimate the app has by then.
 */
function marksOf(listing: ReadyListing, saved: CatalogItem[]): CardMark[] {
  const now = Date.now();
  const favourite = new Set(saved.map(item => item.catalogId));
  const facts = listing.items.map(item => {
    const definition = cardDefinitionNow(item.definition);
    const known: CardFacts = {
      tags: definition?.tags.length ? definition.tags : item.tags,
      added: definition?.added ?? item.published,
      free: definition?.free,
      favourite: favourite.has(item.catalogId),
      id: item.id,
      is2d: item.is2d,
    };
    return { definition, known, fresh: isNew(known, now) };
  });
  const byPlace = proPositions(facts.map(card => card.fresh), listing.read, !listing.more, listing.gallery);
  return facts.map((card, i) => ({ definition: card.definition, isNew: card.fresh, is2d: is2dCard(card.known), pro: isProCard(card.known, byPlace[i], now) }));
}

/**
 * A card as a tap or a drag picks it. A placed model is captioned as the app captions it in a project: its
 * variation's own caption (FurnitureVariation.SerializeInner), not the placeholder a card without a title falls back to.
 */
function pickOf(item: CatalogItem, mark: CardMark | undefined, choice: boolean): CatalogPick {
  const definition = item.definition ? mark?.definition : null;
  const caption = definition ? definition.variationCaption || definition.caption : '';
  return { ...item, ...(caption ? { title: caption } : {}), ...(!choice ? { pro: !!mark?.pro } : {}),
    ...(definition?.firstVariationId ? { variationId: definition.firstVariationId } : {}) };
}

function Card({ item, mark, captions, saved, starred, onPlace, onToggleSaved, chosen, locked, placing = false }: {
  item: CatalogItem; mark: CardMark | undefined; captions: Record<string, string> | null; saved: boolean; starred: boolean;
  onPlace: (item: CatalogPick) => void; onToggleSaved: (item: CatalogItem) => void;
  /** Set in a choice (curtains): whether this card is the one chosen; such a card is not dragged onto the scene. */
  chosen?: boolean;
  /** Why the card cannot be placed here: shown, said on a press, and neither placed nor dragged. */
  locked?: string;
  /** Its placement waits for the model: the card shows it is busy. */
  placing?: boolean;
}) {
  // The name waits for the definition and the app's captions; a card without a definition keeps the catalog's title.
  const definition = item.definition ? mark?.definition : null;
  const pending = definition === undefined || (definition !== null && captions === null);
  const name = definition && captions ? briefName(definition, caption => captionIn(captions, caption)) : null;
  const bold = name ? name.bold || name.rest || item.title : item.title;
  const rest = name && name.bold ? name.rest : '';
  const label = [bold, rest].filter(Boolean).join(' ');
  const choice = chosen !== undefined;
  const placed = pickOf(item, mark, choice);
  const press = () => { if (locked) store.set({ error: locked }); else onPlace(placed); };
  return <div className={`catalog-card${placing ? ' is-placing' : ''}`} role="button" tabIndex={0} draggable={!choice && !locked} aria-pressed={choice ? chosen : undefined}
    aria-disabled={locked ? true : undefined} aria-description={locked} aria-busy={placing || undefined}
    aria-label={pending ? `${item.title}, ${sizeLabel(item)}` : `${label}${mark?.pro ? ', PRO' : ''}, ${sizeLabel(item)}`}
    title={locked ? `${label}. ${locked}` : item.converted ? label : tr('#CatalogDrawerPreparing', label)}
    onDragStart={event => { if (choice || locked) { event.preventDefault(); return; } event.dataTransfer.setData('text/hp-item', dragItemData(placed)); event.dataTransfer.effectAllowed = 'copy'; }}
    onClick={press} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); press(); } }}>
    <span className="catalog-card-image">
      {item.thumb ? <CardPicture src={item.thumb} /> : <Icon name="cube" size={36} />}
      {placing && <span className="catalog-card-placing" aria-hidden="true"><span /></span>}
      {!pending && mark && (mark.isNew || mark.is2d || mark.pro) && <span className="catalog-card-labels" aria-hidden="true">
        {mark.isNew && <span className="catalog-label">NEW</span>}
        {mark.is2d && <span className="catalog-label">2D</span>}
        {mark.pro && <span className="catalog-label-pro"><ProMark /></span>}
      </span>}
    </span>
    {starred && <button type="button" className="catalog-star" aria-pressed={saved} aria-label={saved ? tr('#CatalogDrawerUnsaveNamed', label) : tr('#CatalogDrawerSaveNamed', label)} title={saved ? tr('#CatalogDrawerUnsave') : tr('#CatalogDrawerSave')}
      onClick={event => { event.stopPropagation(); onToggleSaved(item); }} onKeyDown={event => event.stopPropagation()} onDragStart={event => { event.preventDefault(); event.stopPropagation(); }}><Icon name="star" size={20} /></button>}
    <span className="catalog-card-info">
      {pending ? <span className="catalog-card-name is-pending"><i /></span>
        : <span className="catalog-card-name"><b>{bold}</b>{rest && <span>{rest}</span>}</span>}
      <span className="catalog-card-size">{sizeLabel(item)}</span>
    </span>
  </div>;
}

/** «Без штор» (NoCurtainsItem): the first card of the curtains, marked while nothing hangs over the opening. */
function NoCurtainsCard({ choice }: { choice: CurtainChoice }) {
  return <div className="catalog-card catalog-card-none" role="button" tabIndex={0} aria-pressed={choice.bare} aria-label={tr('#NoCurtains')} title={tr('#NoCurtains')}
    onClick={choice.onNone} onKeyDown={event => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); choice.onNone(); } }}>
    <span className="catalog-card-image"><Icon name="window" size={40} /></span>
    <span className="catalog-card-info"><span className="catalog-card-name"><b>{tr('#NoCurtains')}</b></span><span className="catalog-card-size">{'\u00a0'}</span></span>
  </div>;
}

function SkeletonCard() {
  return <div className="catalog-card is-skeleton" aria-hidden="true"><span className="catalog-card-image" /><span className="catalog-card-info"><i /><i /></span></div>;
}

/** The app's PRO mark (Figma «Row labels|Furniture», PRO): the wordmark on a green pill. */
function ProMark() {
  return <svg viewBox="0 0 26.25 7.5" width="26.25" height="7.5" aria-hidden="true" focusable="false"><g fill="#fff" transform="matrix(1 0 0 -1 0 7.5)">
    <path d="M21.2138 7.49315C19.4319 7.33812 18.2477 6.72964 17.5188 5.58243C17.0058 4.78404 16.8669 3.62133 17.1716 2.67179C17.5882 1.35793 18.6913 0.462644 20.3228 0.109956C20.8011 0.00531265 21.8811 -0.0334443 22.4481 0.0324425C24.3265 0.253357 25.6803 1.26104 26.1239 2.77643C26.3784 3.63296 26.2396 4.72978 25.7806 5.52042C25.553 5.91574 24.9359 6.53586 24.5463 6.76452C23.9793 7.09783 23.3468 7.32262 22.6795 7.41951C22.2861 7.47765 21.4954 7.51641 21.2138 7.49315ZM22.3131 5.67545C23.0421 5.52042 23.6361 5.03596 23.8713 4.40422C24.1992 3.50894 23.7981 2.48576 22.9457 2.0323C22.5522 1.82689 22.1742 1.74162 21.6381 1.74162C20.427 1.74162 19.59 2.30747 19.3393 3.29965C19.2622 3.5942 19.2853 4.06316 19.3856 4.37709C19.5977 5.01658 20.2496 5.54368 21.021 5.69095C21.3025 5.74521 22.02 5.73746 22.3131 5.67545Z" />
    <path d="M0 3.77635V0.230086H1.07998H2.15996V1.17576V2.1253L3.83007 2.13693C5.38061 2.15243 5.5156 2.16018 5.78946 2.23382C6.97358 2.56325 7.67556 3.47017 7.67556 4.67163C7.67556 5.91185 6.96586 6.83039 5.74703 7.1637C5.26875 7.29548 4.68248 7.3226 2.28724 7.3226H0V3.77635ZM4.89847 5.4894C5.1029 5.40801 5.31118 5.2181 5.38446 5.04757C5.46546 4.86154 5.45389 4.48172 5.36518 4.30344C5.26875 4.11741 5.05661 3.93137 4.87147 3.86936C4.77119 3.83448 4.3122 3.8151 3.44436 3.80735H2.15996V4.68713V5.57079H3.44436C4.54748 5.55916 4.74805 5.54754 4.89847 5.4894Z" />
    <path d="M8.58968 7.29936C8.57425 7.28386 8.56268 5.68707 8.56268 3.74922V0.230095L10.7303 0.230088V1.17189L10.7419 2.10981H11.7062H12.6666L13.3262 1.17964L14.0157 0.230095H16.4388L16.3038 0.412253C16.2305 0.516896 15.8641 1.02849 15.4938 1.55558L14.815 2.51288L15.0348 2.6369C15.1544 2.70666 15.3858 2.89657 15.5478 3.05547C16.0222 3.53218 16.2382 4.05153 16.2382 4.7259C16.2382 5.63669 15.8178 6.41958 15.1043 6.85365C14.8613 7.00093 14.3637 7.17921 14.0204 7.24898C13.7581 7.29936 8.63982 7.34974 8.58968 7.29936ZM13.3262 5.52042C13.7697 5.40414 13.9703 5.18323 13.9934 4.77628C14.0204 4.34608 13.8353 4.03215 13.4612 3.88487C13.3107 3.82674 13.1102 3.81511 12.007 3.80348H10.7226V4.68327V5.57855H11.9106C12.8594 5.57855 13.1449 5.56692 13.3262 5.52042Z" />
  </g></svg>;
}

/** The tree under a top section: folders open onto their lists, a list is chosen with a click. */
function TreeRows({ nodes, path, depth, expanded, selectedId, onToggle, onChoose }: {
  nodes: CatalogNode[]; path: CatalogNode[]; depth: number; expanded: Set<string>; selectedId: string; onToggle: (id: string) => void; onChoose: (entry: ListEntry) => void;
}) {
  const rowClass = depth ? 'catalog-list' : 'catalog-category';
  return <>{nodes.map(node => {
    if (node.children?.length) {
      const open = expanded.has(node.id);
      return <div key={node.id} className="catalog-branch">
        <button type="button" className={rowClass} aria-expanded={open} onClick={() => onToggle(node.id)}><span>{node.title}</span><Icon name="chevronDown" size={12} /></button>
        <div className="catalog-subtree" data-expanded={open} inert={!open}><div>
          {open && <TreeRows nodes={node.children} path={[...path, node]} depth={depth + 1} expanded={expanded} selectedId={selectedId} onToggle={onToggle} onChoose={onChoose} />}
        </div></div>
      </div>;
    }
    return <button key={node.id} type="button" className={rowClass} aria-current={node.id === selectedId || undefined} onClick={() => onChoose({ node, path })}><span>{node.title}</span></button>;
  })}</>;
}

/** A suggested category as a round picture with its name. */
function Tile({ target, onChoose }: { target: CategoryTarget; onChoose: (target: CategoryTarget) => void }) {
  const photo = photoOf(target);
  return <button type="button" className="catalog-tile" onMouseDown={keepFocus} onClick={() => onChoose(target)}>
    <span className="catalog-tile-picture">{photo && <img src={photo} alt="" loading="lazy" decoding="async" />}</span>
    <span>{target.title}</span>
  </button>;
}

/** A card's picture fades in once decoded; the card keeps its size meanwhile. */
function CardPicture({ src }: { src: string }) {
  const [loaded, setLoaded] = useState(false);
  // With CORS, as the rest of the editor asks the file host (see MaterialImage).
  return <img src={src} alt="" crossOrigin="anonymous" loading="lazy" decoding="async" data-loaded={loaded} ref={image => { if (image?.complete && image.naturalWidth) setLoaded(true); }} onLoad={() => setLoaded(true)} />;
}

/** Only real doors and windows replace an opening: arches and gaps are drawn, not placed. */
function fitting(items: CatalogItem[], kind?: FixedKind): CatalogItem[] {
  if (!kind) return items;
  if (kind === 'curtains') return items.filter(item => item.tags.includes('Curtains'));
  return items.filter((item) => {
    const tags = item.tags.map((t) => t.toLowerCase());
    if (tags.includes('arch') || tags.includes('gap')) return false;
    return kind === 'door' ? tags.some((t) => t.includes('door')) : tags.includes('window');
  });
}
const withFitting = (page: CatalogPage, kind?: FixedKind): CatalogPage => kind ? { ...page, items: fitting(page.items, kind) } : page;
